import clsx from 'clsx';
import _ from 'lodash';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { SelectItem } from '@/core/SelectItem.organism';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { faChartArea, faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, Icon } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export type ItemSelectValue =
  | {
      id: string;
    }
  | string
  | string[];

export interface ItemSelectBase extends Omit<ValidatingFormComponent<ItemSelectValue>, 'onChange'> {
  label?: string;
  /** true if the select can be toggled between a text input field and a select */
  textEntryAllowed?: boolean;
  textPlaceholder?: string;
  /** if true then only text input will be displayed - so this only applies if textEntryAllowed is set to true */
  textToggleDisabled?: boolean;
  selectPlaceholder?: string;
  insideModal?: boolean;
  excludeWorkbookItems?: boolean;
  /** true does not automatically select even if there's only one item */
  disableAutoSelect?: boolean;
  /** Set to false to hide the "add to display pane" icon */
  showAddToDisplayPane?: boolean;
  /** true to include metadata, or a function that takes the selected item and returns whether to fetch metadata */
  includeMetadata?: boolean | ((item: any) => boolean);
  excludeStringSignals?: boolean;
  includeOnlyStringSignals?: boolean;
  includeOnlyStepSignals?: boolean;
  isMultipleSelect?: boolean;
  excludedIds?: string[];
  additionalItems?: object[];
  /** to limit items to only certain item types */
  itemTypes?: string[];
  /** index used to set the threshold in scorecard */
  thresholdLevel?: string;
  /** used by formula, the name */
  identifier?: string;
  /** used by formula, index */
  paramIndex?: string;
  items?: object[];
  allowClear?: boolean;
  /** Determines if the select element is allowed to wrap when the name is long */
  noWrap?: boolean;
  value:
    | {
        id: string;
      }
    | string
    | string[];
  onChange?: (
    selectedItemOrValue:
      | {
          id: string;
          name?: string;
        }
      | string,
    level?: number | string,
  ) => void;
  onRemove?: (selectedItem: object) => void;
  customErrorText?: string;
  /** Function to add all items in the details pane to the item select, button will not appear if undefined */
  onDetailsButtonClick?: () => void;
  addAllEnabled?: boolean;
  removeAllEnabled?: boolean;
  stopPropagation?: boolean;
  closeMenuOnSelect?: boolean;
  onRemoveAll?: () => void;
  onAddAllClick?: () => void;
  showOnlyCalculatedItems?: boolean;
  onAddItemsClick?: () => void;
  addItemsLabel?: string;
  addItemsEnabled?: boolean;
}

export interface ItemSelectIF extends ItemSelectBase {
  component: 'ItemSelectFormComponent';
}

export const ItemSelectFormComponent = (props: ItemSelectIF) => {
  const {
    name,
    validation,
    label,
    testId = '',
    value,
    textPlaceholder,
    selectPlaceholder,
    extendValidation,
    extraClassNames,
    customErrorText,
    isMultipleSelect,
    onDetailsButtonClick,
    required = true,
    addAllEnabled = false,
    removeAllEnabled = false,
    closeMenuOnSelect = true,
    onRemoveAll,
    onAddAllClick,
    showOnlyCalculatedItems,
    tooltip,
    tooltipPlacement,
    addItemsLabel,
    onAddItemsClick,
    addItemsEnabled = false,
    showAddToDisplayPane = true,
  } = props;
  const { t } = useTranslation();
  const defaultValidation = (selected: ItemSelectValue | undefined) =>
    required && (_.isEmpty(selected) || (selected as { id?: string } | undefined)?.id === '');
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const form = useForm();
  const formState = form.getState();
  const { errors, hasValidationErrors, modified, submitFailed } = useFormState({
    subscription: { errors: true, hasValidationErrors: true, modified: true, submitFailed: true },
  });
  const showError =
    _.has(errors, name) &&
    hasValidationErrors &&
    ((modified?.[name] as boolean | undefined) || submitFailed || !!props.defaultProvided);

  const resetField = () => {
    form.change(name, undefined);
    form.resetFieldState(name);
  };

  const getInputValueId = (inputValue: ItemSelectValue | undefined) =>
    _.isObject(inputValue) && !Array.isArray(inputValue) ? (inputValue as { id?: string }).id : (inputValue as string);
  const getSelectedItem = (value: ItemSelectValue | undefined, inputValue: ItemSelectValue | undefined) => {
    // in some cases the value is a number, in some cases it's an id and sometimes it's an object - so we do this
    return value !== '' && inputValue !== '' && inputValue !== undefined ? getInputValueId(inputValue) : value;
  };

  const showLabel = label || (onDetailsButtonClick && isMultipleSelect);

  return (
    <FormFieldWrapper
      id={name}
      testId={testId}
      wrapperClassNames={clsx('flexFill', extraClassNames)}
      showError={showError}
      customErrorText={customErrorText}
      label={
        showLabel && (
          <div
            className="flexColumnContainer flexAlignBottom flexFill tw:justify-between tw:pb-0.5"
            data-testid={`${testId}Label`}>
            <div className="flexColumnContainer tw:mb-sq-5 tw:items-center">
              <span className={clsx('tw:mr-sq-5')}>{label ? t(label) : ''}</span>
              {tooltip && (
                <Icon icon={faQuestionCircle} large={true} tooltip={t(tooltip)} tooltipPlacement={tooltipPlacement} />
              )}
            </div>
            {onDetailsButtonClick && isMultipleSelect && (
              <Button
                icon={faChartArea}
                size="xs"
                label={t('DETAILS')}
                tooltip={t('ADD_FROM_DETAILS_PANE')}
                onClick={onDetailsButtonClick}
              />
            )}
          </div>
        )
      }>
      <Field<ItemSelectValue> name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const selectedValue = isMultipleSelect
            ? _.map(
                (value as (ItemSelectValue | undefined)[] | undefined) ?? [],
                (item: ItemSelectValue | undefined, index: number) =>
                  getSelectedItem(
                    item,
                    Array.isArray(input.value) ? (input.value as (ItemSelectValue | undefined)[])[index] : undefined,
                  ),
              )
            : getSelectedItem(value, input.value as ItemSelectValue | undefined);
          const formFieldProps = getFormFieldProps(formState, input, meta, props);

          return (
            <SelectItem
              {...formFieldProps}
              onSelect={(
                item:
                  | string
                  | {
                      id: string;
                      name?: string;
                    },
                level?: number | string,
              ) => {
                props.onChange?.(item, level);
                input.onChange(item);
              }}
              showError={formFieldProps.showError}
              selectedItemId={selectedValue}
              selectPlaceholder={selectPlaceholder}
              textPlaceholder={textPlaceholder}
              onToggle={() => resetField()}
              isMultipleSelect={isMultipleSelect}
              addAllEnabled={addAllEnabled}
              removeAllEnabled={removeAllEnabled}
              testId={testId}
              closeMenuOnSelect={isMultipleSelect ? false : closeMenuOnSelect}
              onRemoveAll={onRemoveAll}
              onAddAllClick={onAddAllClick}
              showOnlyCalculatedItems={showOnlyCalculatedItems}
              onAddItemsClick={onAddItemsClick}
              addItemsLabel={addItemsLabel}
              addItemsEnabled={addItemsEnabled}
              showAddToDisplayPane={showAddToDisplayPane}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
